import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ToggleNumberRow } from '~/components/toolbar/shared/StageToggleRows';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { toOperationOutcome } from '~/hooks/structure-set/operationOutcome';
import { PostProcessingMode } from '~/engine/util/algorithms/sdf/SDFPostProcessing';
import ModeToggle from '~/components/toolbar/OperationsToolbar/ModeToggle';
import OperationApplyButton from '~/components/toolbar/OperationsToolbar/OperationApplyButton';
import ShowPreviewToggle from '~/components/toolbar/OperationsToolbar/ShowPreviewToggle';
import { AUTO_PREVIEW_DELAY_MS } from '~/components/toolbar/OperationsToolbar/autoPreview';
import PreviewResultStatus from '~/components/toolbar/OperationsToolbar/PreviewResultStatus';
import { usePostProcessingPreview } from '~/hooks/structure-set/usePostProcessingPreview';
import { useRoiSdfOperation } from '~/hooks/structure-set/useRoiSdfOperation';

// Size-floor thresholds by volume (cm³) in 3D and by in-plane area (cm²) in
// 2D, so each mode carries its own sensible default (matches the retired
// standalone RemoveSmallPartsPanel's constants — same physical meaning,
// same numbers).
const DEFAULT_MIN_VOLUME_CM3 = 0.5;
const DEFAULT_MIN_AREA_CM2 = 0.1;
/**
 * Per-slice, not whole-volume, on open. Every mode toggle in this panel
 * starts 2D for one reason: 2D is the lower-blast-radius direction. A 3D
 * pass reaches through slices the user has not looked at — a 3D
 * keep-largest can drop an island fifteen slices away — and an effect that
 * cannot be checked is the hazard, not the wrong answer. The rectangle
 * erase already defaults this way (SR-WOR-H-24-1).
 *
 * Deliberately a per-tab constant rather than one shared across the panel:
 * the tabs' modes are independent settings and one flipping is not a
 * reason for the others to.
 */
const DEFAULT_POST_PROCESSING_MODE: PostProcessingMode = '2D';
const DEFAULT_KEEP_COUNT = 1;

// ── Parts tab content ────────────────────────────────────────────────

export default function PartsTab() {
    const { isProcessing, executeOperation } = useRoiSdfOperation();
    const {
        isPreviewing,
        isPreviewReady,
        isPreviewVisible,
        setPreviewVisible,
        preview,
        clearPreview,
        invalidatePreview,
        sdfRevision,
    } = usePostProcessingPreview();
    const [mode, setMode] = useState<PostProcessingMode>(
        DEFAULT_POST_PROCESSING_MODE
    );
    const [keepLargestEnabled, setKeepLargestEnabled] = useState(false);
    const [keepCount, setKeepCount] = useState(DEFAULT_KEEP_COUNT);
    const [minSizeEnabled, setMinSizeEnabled] = useState(false);
    // Threshold is a volume (cm³) in 3D and an in-plane area (cm²) in 2D,
    // so the initial value has to follow the initial MODE — seeding it with
    // the cm³ default while the toggle reads 2D would state 0.50 cm² on
    // open, five times the 2D default, in the mode where the smallest parts
    // are judged.
    const [minSize, setMinSize] = useState(
        DEFAULT_POST_PROCESSING_MODE === '2D'
            ? DEFAULT_MIN_AREA_CM2
            : DEFAULT_MIN_VOLUME_CM3
    );
    const isArea = mode === '2D';
    const eitherEnabled = keepLargestEnabled || minSizeEnabled;
    // True once a preview has run and found no part to remove — a floor
    // below the smallest region present, or an N no smaller than the
    // number of parts. The result is then identical to the structure as it
    // stands, which on screen is the same picture as no preview at all.
    const [previewFoundNothing, setPreviewFoundNothing] = useState(false);

    // One decision point, read by both the preview and the apply path, so
    // the overlay can never show a different operation from the one Apply
    // runs. keepLargestComponents already fuses the size floor into its
    // single labeling pass, so "both on" is one call, not two.
    const runOperation = useCallback(
        (texture: ContourTexture, signal?: AbortSignal) => {
            // Apply passes no signal: there is nothing to cancel a commit
            // with. Both engine calls take an optional signal, so an
            // undefined one is never aborted.
            if (keepLargestEnabled) {
                return SDFOperations.applyKeepLargestComponents(
                    texture,
                    keepCount,
                    mode,
                    minSizeEnabled ? minSize : undefined,
                    signal
                );
            }
            return SDFOperations.applyRemoveSmallParts(
                texture,
                minSize,
                mode,
                signal
            );
        },
        [keepLargestEnabled, keepCount, minSizeEnabled, minSize, mode]
    );

    // Identifies the settings the on-screen result belongs to, so an apply
    // can hand the previewed texture off instead of recomputing.
    const previewKey = [
        'parts',
        mode,
        keepLargestEnabled ? keepCount : 'off',
        minSizeEnabled ? minSize : 'off',
    ].join(':');

    // The claim "this is the result" is dropped by the effect below
    // before this ever runs.
    const runPreview = useCallback(() => {
        setPreviewFoundNothing(false);
        preview(
            async (srcTexture, signal) => {
                const result = await runOperation(srcTexture, signal);
                // The engine's own count. Both branches of runOperation report
                // it, so "kept everything" is stated rather than left to a
                // viewport that looks untouched either way. Gated on the
                // signal: a superseded run must not state its outcome over a
                // newer run's overlay.
                if (!signal.aborted) {
                    setPreviewFoundNothing(result?.status === 'unchanged');
                }
                // Handed on whole: the preview reads the tag itself, so a
                // pass that removed nothing unlocks Apply rather than
                // reading as a compute that failed.
                return result ?? { status: 'failed' };
            },
            previewKey,
            { hidePreviewedStructure: true }
        );
    }, [preview, runOperation, previewKey]);

    // The preview computes itself on every input that changes what
    // `runOperation` would do. `runOperation` is memoised on exactly those
    // inputs (criteria, their values, the mode), so depending on it here is
    // the same list without restating it — plus sdfRevision, for an ROI
    // edited under us. Debounced, because the size threshold is typed and
    // would otherwise queue a GPU compute per digit.
    //
    // Torn down rather than recomputed with neither criterion on: there is
    // no operation then, so an overlay would picture one that does nothing.
    // Skipped during an apply so the commit is not disturbed.
    useEffect(() => {
        if (isProcessing) {
            return;
        }
        if (!eitherEnabled) {
            clearPreview();
            setPreviewFoundNothing(false);
            return;
        }
        // invalidatePreview, not clearPreview: readiness drops
        // SYNCHRONOUSLY here, so Apply cannot commit the previous
        // settings' result during the debounce, while the picture stays up
        // until the recompute swaps it in.
        invalidatePreview();
        setPreviewFoundNothing(false);
        const timeout = setTimeout(() => runPreview(), AUTO_PREVIEW_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [
        isProcessing,
        eitherEnabled,
        sdfRevision,
        runPreview,
        clearPreview,
        invalidatePreview,
    ]);

    const handleModeChange = (nextMode: PostProcessingMode) => {
        setMode(nextMode);
        // The threshold's unit changes with the mode (cm² ↔ cm³), so reset
        // to that mode's default rather than carrying a value that would
        // now mean a different physical size.
        setMinSize(
            nextMode === '2D' ? DEFAULT_MIN_AREA_CM2 : DEFAULT_MIN_VOLUME_CM3
        );
    };

    const handleApply = () => {
        clearPreview();
        setPreviewFoundNothing(false);
        executeOperation({
            operationFn: async (roi) => {
                const result = await runOperation(roi.texture!);
                if (!result) {
                    return { status: 'failed' };
                }
                // The engine's own tag, the same one the preview states: a
                // floor below every region present, or an N no smaller
                // than the number of parts, removes nothing and is
                // therefore not an edit to record.
                return toOperationOutcome(result, {
                    removed: result.numComponentsRemoved,
                    total: result.numComponentsFound,
                    // The never-empty rule can retain a part the size floor
                    // rejected, leaving a structure that violates the
                    // minimum the user set. The count alone cannot reveal
                    // that, so the toast says it.
                    floorOverridden: result.sizeFloorOverridden
                        ? 'true'
                        : 'false',
                });
            },
            // Must name the operation that actually ran, not just the tab:
            // this string lands verbatim in the ROI's undo-history entry
            // (roi.replaceTexture writes it) and in the audit trail, so a
            // clinician reading undo sees what happened, not the tab it
            // happened in. When keepLargestEnabled the call is
            // applyKeepLargestComponents whether or not the floor is fused
            // in — the floor is only a filter inside that pass, so the
            // operation is still "keep largest parts" in both cases.
            operationName: keepLargestEnabled
                ? 'Keep Largest Parts'
                : 'Remove Small Parts',
            busyMessageId: 'postProcessing.parts.busy',
            // In 2D the counts are per-slice parts, not whole 3D parts, so
            // the toast wording differs to avoid overstating the piece count.
            successMessageId: isArea
                ? 'postProcessing.parts.success.2d'
                : 'postProcessing.parts.success',
            failedMessageId: 'postProcessing.parts.failed',
            logLabel: keepLargestEnabled
                ? 'Keep largest parts'
                : 'Remove small parts',
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            {/* Its own explanation: the shared one describes linking
                regions, and everything this tab labels, counts and removes
                is a part. */}
            <ModeToggle
                mode={mode}
                onChange={handleModeChange}
                disabled={isProcessing}
                tooltipId="postProcessing.parts.modeTooltip"
            />
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="postProcessing.parts.description" />
            </Typography>
            <ToggleNumberRow
                labelId="postProcessing.parts.keepLargest"
                tooltipId="postProcessing.parts.keepLargestTooltip"
                enabled={keepLargestEnabled}
                onEnabledChange={setKeepLargestEnabled}
                value={keepCount}
                onValueChange={setKeepCount}
                min={1}
                step={1}
                precision={0}
                snapToStep
                dataTestId="post-processing-parts-keep-largest"
            />
            <ToggleNumberRow
                labelId={
                    isArea
                        ? 'postProcessing.parts.minArea'
                        : 'postProcessing.parts.minVolume'
                }
                tooltipId="postProcessing.parts.minSizeTooltip"
                enabled={minSizeEnabled}
                onEnabledChange={setMinSizeEnabled}
                value={minSize}
                onValueChange={setMinSize}
                min={0}
                // The step follows the MODE, as the default and the unit
                // already do: 0.5 is the 3D default (cm³), 0.1 the 2D one
                // (cm²). A fixed 0.5 stepped the 2D floor from its own
                // default straight to 0.6 — five defaults in one arrow
                // press, in the mode where the smallest parts are judged.
                step={isArea ? DEFAULT_MIN_AREA_CM2 : DEFAULT_MIN_VOLUME_CM3}
                precision={2}
                unit={isArea ? 'cm²' : 'cm³'}
                dataTestId="post-processing-parts-min-size"
            />
            {/* The one refusal of this tab's own, stated in words beside
                the button it holds: with neither criterion enabled there
                is no operation to preview or commit, and a greyed Apply
                alone does not say which. */}
            {!eitherEnabled && (
                <Typography variant="body2" color="warning.main">
                    <FormattedMessage id="postProcessing.parts.noCriterion" />
                </Typography>
            )}
            <ShowPreviewToggle
                visible={isPreviewVisible}
                onChange={setPreviewVisible}
                disabled={!isPreviewReady}
            />
            <OperationApplyButton
                onApply={handleApply}
                isPreviewing={isPreviewing}
                isPreviewReady={isPreviewReady}
                isProcessing={isProcessing}
                applyDisabled={!eitherEnabled}
            />
            <PreviewResultStatus
                isPreviewOnScreen={isPreviewReady}
                previewChangesNothing={previewFoundNothing}
            />
        </Box>
    );
}
